/**
 * BranchingControls — composants éditeur pour la logique conditionnelle XLSForm.
 *
 * Stratégie : aucun changement du moteur ni du modèle. On pilote uniquement le
 * champ `relevant` existant (item, section, bloc). 3 niveaux complémentaires :
 *
 *   B — `BranchEditor` : sous chaque choix d'un `select_one` / `select_multiple`,
 *       un bouton « + Suite si choisi » qui crée AUTOMATIQUEMENT une section
 *       conditionnelle ciblée. Le créateur ne voit JAMAIS l'expression XLSForm.
 *       Convention : la section a l'id `branch__<item_name>__<choice_value>` et
 *       le relevant `${item_name} == "value"` (ou `selected(${item_name}, "value")`
 *       pour les multi-choix). C'est ce qui permet de re-mapper une branche à
 *       son choix à l'ouverture d'une enquête existante.
 *
 *   A — `ConditionPicker` : pour les sections dont la condition n'est pas une
 *       égalité simple sur une question à choix (âge > 18, multi-choix coché,
 *       comparaison numérique). Picker question + opérateur + valeur, génère le
 *       `relevant` correspondant. Une 2e condition optionnelle (ET / OU).
 *
 *   C — `ExpertRelevantInput` : input texte XLSForm AVEC validation live du
 *       moteur (parse) + picker de référence (boutons cliquables pour insérer
 *       `${q_xxx}` au curseur). Le mode expert n'est révélé que via "Mode
 *       avancé" pour ne pas surcharger l'UI 90% du temps.
 *
 *   Lint WARDEN — `WardenLintBadge` : détecte les conditions exposant une
 *   question art. 9 (santé/orientation/infractions) déclenchée par une autre
 *   question art. 9 sans page ressource avant. Avertit l'éditeur (non bloquant
 *   en draft ; à bloquer côté publish dans un second temps).
 */
import { createContext, useContext, useMemo, useRef, useState } from 'react';
// `extractVariables` parse l'expression sans l'évaluer — idéal pour valider la
// syntaxe sans avoir à fournir un jeu de réponses fictif.
import { extractVariables } from '@webforge-survey/shared';
import type { Choice, Item, Section } from '@webforge-survey/shared';

// =============================================================================
//  Context bloc — partagé par BlockEditor → SectionEditor → ItemEditor
// =============================================================================

export interface BranchContextValue {
  /** Sections du bloc parent (lecture). */
  sectionsOfBlock: Section[];
  /** Ajoute une section au bloc (au bout de la liste). */
  addSection: (section: Section) => void;
  /** Supprime la section par id. */
  removeSection: (sectionId: string) => void;
  /** Variables disponibles pour les pickers (items précédents du formulaire). */
  availableVars: Array<{ name: string; label: string; type: Item['type']; sensitive?: boolean }>;
}

const BranchContext = createContext<BranchContextValue | null>(null);

export function BranchContextProvider({
  value,
  children,
}: {
  value: BranchContextValue;
  children: React.ReactNode;
}): JSX.Element {
  return <BranchContext.Provider value={value}>{children}</BranchContext.Provider>;
}

/** Récupère le contexte ; retourne null si l'éditeur n'est pas wrappé. */
export function useBranchContext(): BranchContextValue | null {
  return useContext(BranchContext);
}

// =============================================================================
//  Helpers communs
// =============================================================================

/** Génère le `relevant` d'une branche pour un choix donné d'un item à choix. */
export function relevantForChoice(
  item: { name: string; type: Item['type'] },
  choiceValue: string,
): string {
  if (item.type === 'select_multiple') {
    return `selected(\${${item.name}}, "${choiceValue}")`;
  }
  return `\${${item.name}} == "${choiceValue}"`;
}

/** Convention d'id pour une section-branche : `branch__<item>__<choice>`. */
export function branchSectionId(itemName: string, choiceValue: string): string {
  return `branch__${itemName}__${choiceValue}`;
}

/** Inverse de `branchSectionId` — null si la section n'est pas une branche. */
export function parseBranchSectionId(
  sectionId: string,
): { itemName: string; choiceValue: string } | null {
  const m = sectionId.match(/^branch__([a-zA-Z0-9_]+)__(.+)$/);
  if (!m) return null;
  return { itemName: m[1]!, choiceValue: m[2]! };
}

/** Validation moteur : null = OK, sinon message d'erreur lisible. */
export function validateRelevant(expr: string): string | null {
  const trimmed = expr.trim();
  if (!trimmed) return null;
  try {
    extractVariables(trimmed);
    return null;
  } catch (err) {
    return (err as Error)?.message ?? 'Expression invalide';
  }
}

// =============================================================================
//  B · BranchEditor — sous chaque choix d'un select_one/multiple
// =============================================================================

export interface BranchEditorProps {
  /** Item à choix dont on configure les branches. */
  item: Extract<Item, { type: 'select_one' | 'select_multiple' }>;
  /** Sections du bloc parent — utilisées pour savoir si une branche existe déjà. */
  sectionsOfBlock: Section[];
  /** Crée une nouvelle section dans le bloc parent (au bout de la liste). */
  onAddSection: (section: Section) => void;
  /** Supprime la section par id. */
  onRemoveSection: (sectionId: string) => void;
}

export function BranchEditor(props: BranchEditorProps): JSX.Element | null {
  const { item, sectionsOfBlock, onAddSection, onRemoveSection } = props;
  if (!item.choices || item.choices.length === 0) return null;

  const branchByChoice = useMemo(() => {
    const map = new Map<string, Section>();
    for (const s of sectionsOfBlock) {
      const parsed = parseBranchSectionId(s.id);
      if (parsed && parsed.itemName === item.name) {
        map.set(parsed.choiceValue, s);
      }
    }
    return map;
  }, [sectionsOfBlock, item.name]);

  const createBranch = (choice: Choice): void => {
    const sectionId = branchSectionId(item.name, choice.value);
    if (sectionsOfBlock.some((s) => s.id === sectionId)) return; // déjà créée
    onAddSection({
      id: sectionId,
      title: `Si ${choice.label || choice.value}`,
      relevant: relevantForChoice(item, choice.value),
      items: [],
    });
  };

  const removeBranch = (sectionId: string): void => {
    if (
      !confirm(
        'Supprimer cette branche ? Toutes les questions qu\'elle contient seront perdues.',
      )
    )
      return;
    onRemoveSection(sectionId);
  };

  return (
    <div className="mt-3 rounded-md border border-dashed border-border bg-bg-deep/30 p-3">
      <div className="text-xs font-medium text-text-muted mb-2">
        Branches conditionnelles
        <span className="font-normal text-text-subtle"> · une suite de questions par choix</span>
      </div>
      <ul className="space-y-1.5">
        {item.choices.map((c, i) => {
          const branch = branchByChoice.get(c.value);
          return (
            <li
              key={i}
              className="flex items-center gap-2 text-sm rounded-md px-2 py-1.5 bg-surface"
            >
              <span
                className="inline-block w-3 h-3 rounded-full border-2 border-text-subtle shrink-0"
                aria-hidden="true"
              />
              <span className="flex-1 truncate">{c.label || c.value}</span>
              {branch ? (
                <>
                  <span className="text-xs text-text-subtle truncate">
                    → {branch.items?.length ?? 0} question{(branch.items?.length ?? 0) > 1 ? 's' : ''}
                  </span>
                  <button
                    type="button"
                    className="btn-ghost text-xs text-danger"
                    onClick={() => removeBranch(branch.id)}
                    aria-label={`Supprimer la branche pour ${c.label || c.value}`}
                  >
                    Supprimer branche
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="btn-ghost text-xs"
                  onClick={() => createBranch(c)}
                  aria-label={`Créer une branche pour ${c.label || c.value}`}
                >
                  + Suite si choisi
                </button>
              )}
            </li>
          );
        })}
      </ul>
      <p className="text-[11px] text-text-subtle mt-2">
        Chaque branche est une section conditionnelle dans le bloc parent. Le moteur
        XLSForm masque ses questions sauf si le choix est coché — aucune réponse
        n'est collectée pour les branches non déclenchées (minimisation RGPD).
      </p>
    </div>
  );
}

// =============================================================================
//  A · ConditionPicker — picker visuel pour générer un `relevant`
// =============================================================================

const OPERATORS = [
  { value: 'eq', label: 'est égal à', tpl: (ref: string, v: string) => `${ref} == "${v}"` },
  { value: 'neq', label: 'est différent de', tpl: (ref: string, v: string) => `${ref} != "${v}"` },
  { value: 'gt', label: 'est supérieur à', tpl: (ref: string, v: string) => `${ref} > ${v}` },
  { value: 'gte', label: 'est supérieur ou égal à', tpl: (ref: string, v: string) => `${ref} >= ${v}` },
  { value: 'lt', label: 'est inférieur à', tpl: (ref: string, v: string) => `${ref} < ${v}` },
  { value: 'lte', label: 'est inférieur ou égal à', tpl: (ref: string, v: string) => `${ref} <= ${v}` },
  { value: 'selected', label: 'est coché (multi-choix)', tpl: (ref: string, v: string) => `selected(${ref}, "${v}")` },
] as const;
type OpKey = (typeof OPERATORS)[number]['value'];

export interface ConditionPickerProps {
  /** Variables disponibles (questions précédentes du formulaire). */
  availableVars: Array<{ name: string; label: string; type: Item['type'] }>;
  /** Valeur actuelle du `relevant` (peut être vide ou non-éditable par picker). */
  value: string;
  onChange: (next: string) => void;
}

interface ConditionAtom {
  varName: string;
  op: OpKey;
  cmpValue: string;
}

/**
 * Picker visuel pour une condition simple (ou composée AND/OR à 2 atomes).
 * Si le `relevant` actuel n'est pas reconnaissable comme « atome simple » +
 * éventuel opérateur logique, le picker s'affiche en mode "modifié à la main —
 * ouvrez le mode expert pour éditer".
 */
export function ConditionPicker(props: ConditionPickerProps): JSX.Element {
  const { availableVars, value, onChange } = props;
  const parsed = useMemo(() => tryParseSimpleCondition(value), [value]);

  // État initial : si parsing OK on l'utilise, sinon on garde des valeurs vides.
  const initial: ConditionAtom = parsed?.atoms[0] ?? {
    varName: availableVars[0]?.name ?? '',
    op: 'eq',
    cmpValue: '',
  };
  const [a1, setA1] = useState<ConditionAtom>(initial);
  const [joiner, setJoiner] = useState<'and' | 'or' | null>(parsed?.joiner ?? null);
  const [a2, setA2] = useState<ConditionAtom>(
    parsed?.atoms[1] ?? {
      varName: availableVars[0]?.name ?? '',
      op: 'eq',
      cmpValue: '',
    },
  );

  const buildExpr = (
    atom1: ConditionAtom,
    j: 'and' | 'or' | null,
    atom2: ConditionAtom,
  ): string => {
    const opDef1 = OPERATORS.find((o) => o.value === atom1.op)!;
    const expr1 = opDef1.tpl(`\${${atom1.varName}}`, atom1.cmpValue);
    if (!j) return expr1;
    const opDef2 = OPERATORS.find((o) => o.value === atom2.op)!;
    const expr2 = opDef2.tpl(`\${${atom2.varName}}`, atom2.cmpValue);
    const sep = j === 'and' ? ' && ' : ' || ';
    return `(${expr1})${sep}(${expr2})`;
  };

  const update = (atom1: ConditionAtom, j: 'and' | 'or' | null, atom2: ConditionAtom): void => {
    setA1(atom1);
    setJoiner(j);
    setA2(atom2);
    onChange(buildExpr(atom1, j, atom2));
  };

  if (availableVars.length === 0) {
    return (
      <p className="text-xs text-text-subtle">
        Aucune question précédente disponible — cette section sera toujours affichée.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      <AtomRow
        availableVars={availableVars}
        atom={a1}
        onChange={(next) => update(next, joiner, a2)}
      />
      {joiner !== null && (
        <>
          <div className="flex items-center gap-2">
            <label className="text-xs text-text-muted">Joindre avec</label>
            <select
              className="input-base text-sm w-auto"
              value={joiner}
              onChange={(e) => update(a1, e.target.value as 'and' | 'or', a2)}
            >
              <option value="and">ET (toutes les conditions)</option>
              <option value="or">OU (au moins une)</option>
            </select>
            <button
              type="button"
              className="btn-ghost text-xs text-danger"
              onClick={() => update(a1, null, a2)}
            >
              ✕ retirer la 2e condition
            </button>
          </div>
          <AtomRow
            availableVars={availableVars}
            atom={a2}
            onChange={(next) => update(a1, joiner, next)}
          />
        </>
      )}
      {joiner === null && (
        <button
          type="button"
          className="btn-ghost text-xs"
          onClick={() => update(a1, 'and', a2)}
        >
          + Ajouter une condition (ET / OU)
        </button>
      )}
      <p className="text-[11px] text-text-subtle font-mono">
        → relevant : <code className="bg-bg-deep/40 px-1 rounded">{buildExpr(a1, joiner, a2) || '—'}</code>
      </p>
    </div>
  );
}

function AtomRow({
  availableVars,
  atom,
  onChange,
}: {
  availableVars: ConditionPickerProps['availableVars'];
  atom: ConditionAtom;
  onChange: (next: ConditionAtom) => void;
}): JSX.Element {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-sm text-text-muted">Si</span>
      <select
        className="input-base text-sm w-auto max-w-[18rem]"
        value={atom.varName}
        onChange={(e) => onChange({ ...atom, varName: e.target.value })}
      >
        {availableVars.map((v) => (
          <option key={v.name} value={v.name}>
            {v.label ? `${v.label} (${v.name})` : v.name}
          </option>
        ))}
      </select>
      <select
        className="input-base text-sm w-auto"
        value={atom.op}
        onChange={(e) => onChange({ ...atom, op: e.target.value as OpKey })}
      >
        {OPERATORS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <input
        type="text"
        className="input-base text-sm w-32"
        value={atom.cmpValue}
        onChange={(e) => onChange({ ...atom, cmpValue: e.target.value })}
        placeholder="valeur"
      />
    </div>
  );
}

/** Best-effort : reconnaît `${q} op "v"`, `selected(${q}, "v")`, ou (...)&&|||(...) */
function tryParseSimpleCondition(
  expr: string,
): { atoms: ConditionAtom[]; joiner: 'and' | 'or' | null } | null {
  const trimmed = expr.trim();
  if (!trimmed) return null;
  // Atome simple.
  const a = parseAtom(trimmed);
  if (a) return { atoms: [a], joiner: null };
  // Composé : (atom) && (atom) ou (atom) || (atom).
  const cm = trimmed.match(/^\((.+)\)\s*(&&|\|\|)\s*\((.+)\)$/);
  if (cm) {
    const a1 = parseAtom(cm[1]!.trim());
    const a2 = parseAtom(cm[3]!.trim());
    if (a1 && a2) {
      return { atoms: [a1, a2], joiner: cm[2] === '&&' ? 'and' : 'or' };
    }
  }
  return null;
}

function parseAtom(s: string): ConditionAtom | null {
  // selected(${q}, "v")
  const sel = s.match(/^selected\(\$\{([a-z_][a-z0-9_]*)\}\s*,\s*"([^"]*)"\)$/i);
  if (sel) return { varName: sel[1]!, op: 'selected', cmpValue: sel[2]! };
  // ${q} OP "v" ou ${q} OP nb
  const cmp = s.match(/^\$\{([a-z_][a-z0-9_]*)\}\s*(==|!=|>=|<=|>|<)\s*(.+)$/i);
  if (cmp) {
    const opMap: Record<string, OpKey> = {
      '==': 'eq',
      '!=': 'neq',
      '>': 'gt',
      '>=': 'gte',
      '<': 'lt',
      '<=': 'lte',
    };
    const raw = cmp[3]!.trim();
    const strMatch = raw.match(/^"([^"]*)"$/);
    const value = strMatch ? strMatch[1]! : raw;
    return { varName: cmp[1]!, op: opMap[cmp[2]!]!, cmpValue: value };
  }
  return null;
}

// =============================================================================
//  C · ExpertRelevantInput — input XLSForm + validation moteur + picker de ref
// =============================================================================

export interface ExpertRelevantInputProps {
  value: string;
  onChange: (next: string) => void;
  availableVars: ConditionPickerProps['availableVars'];
  placeholder?: string;
  ariaLabel?: string;
}

export function ExpertRelevantInput(props: ExpertRelevantInputProps): JSX.Element {
  const { value, onChange, availableVars, placeholder, ariaLabel } = props;
  const inputRef = useRef<HTMLInputElement>(null);
  const error = useMemo(() => validateRelevant(value), [value]);

  const insertRef = (varName: string): void => {
    const el = inputRef.current;
    if (!el) {
      onChange(value + `\${${varName}}`);
      return;
    }
    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? value.length;
    const insert = `\${${varName}}`;
    const next = value.slice(0, start) + insert + value.slice(end);
    onChange(next);
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + insert.length;
      el.setSelectionRange(pos, pos);
    });
  };

  return (
    <div className="space-y-1">
      <input
        ref={inputRef}
        type="text"
        className={`input-base font-mono text-sm ${error ? 'border-danger' : ''}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder ?? '${b1q1} == "oui"'}
        aria-label={ariaLabel}
        aria-invalid={error ? 'true' : 'false'}
      />
      {error && (
        <p className="text-xs text-danger" role="alert">
          Expression invalide : {error}
        </p>
      )}
      {availableVars.length > 0 && (
        <div className="flex items-center gap-1 flex-wrap text-xs">
          <span className="text-text-subtle mr-1">Insérer :</span>
          {availableVars.slice(0, 8).map((v) => (
            <button
              key={v.name}
              type="button"
              className="font-mono text-[11px] px-1.5 py-0.5 rounded border border-border bg-surface hover:bg-bg-deep/40"
              onClick={() => insertRef(v.name)}
              title={v.label || v.name}
            >
              ${'{'}{v.name}{'}'}
            </button>
          ))}
          {availableVars.length > 8 && (
            <span className="text-text-subtle">+ {availableVars.length - 8} autres</span>
          )}
        </div>
      )}
    </div>
  );
}

// =============================================================================
//  WardenLintBadge — détection art. 9 conditionnée par art. 9
// =============================================================================

export function WardenLintBadge({
  relevant,
  availableVars,
  ownerSensitive,
}: {
  relevant: string;
  availableVars: ConditionPickerProps['availableVars'];
  /** True si l'élément (item ou section) que cette condition gouverne est sensitive. */
  ownerSensitive: boolean;
}): JSX.Element | null {
  // Cherche les `${q_xxx}` référencés et flag si l'un d'eux est sensitive.
  const refs = Array.from(relevant.matchAll(/\$\{([a-z_][a-z0-9_]*)\}/gi)).map(
    (m) => m[1]!,
  );
  const sensitiveTrigger = refs.find((name) => {
    const v = availableVars.find((x) => x.name === name);
    // availableVars n'expose pas meta.sensitive ; on s'en remet à un convention
    // de nommage des variables sensibles si dispo, ou on laisse l'utilisateur juger.
    return v && (v as { sensitive?: boolean }).sensitive === true;
  });
  if (!sensitiveTrigger || !ownerSensitive) return null;
  return (
    <p
      className="text-xs text-warning bg-warning/10 border border-warning/40 rounded px-2 py-1 mt-1"
      role="alert"
    >
      ⚠ WARDEN — cette condition expose une donnée sensible (art. 9) à partir
      d'une autre donnée sensible <code>{sensitiveTrigger}</code>. Vérifie qu'une
      page ressource (info / orientation) précède bien cette branche dans le
      parcours du répondant.
    </p>
  );
}
